import settings from '@solinkcorp/settings';
import { dateToDay, dayToLocalDate, endOfDayToDate } from '@solinkcorp/solink-components';
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  RadioGroup,
  RadioGroupItem,
  SelectItem,
  SimpleSelect,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@solinkcorp/solink-ui';
import { datePickerLocale } from 'components/GlobalDatePicker/Aspen/datePickerTime';
import {
  AI_CHAT_WEEKLY_SCHEDULE_OPTIONS,
  AI_INSPECTION_END_TYPE,
  type AiInspectionEndType,
  type UseSpotCheckForm,
} from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import { Info } from 'lucide-react';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import {
  DOES_NOT_REPEAT,
  MAX_HOURLY_OCCURRENCES_PER_2_YEARS,
  REPEAT_DAILY,
  REPEAT_EVERY_15MIN,
  REPEAT_EVERY_30MIN,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
} from 'vd-constants';
import { getFormattedScheduledSummary } from '../SpotCheckMainContent/SpotCheckFormView/Aspen/spotCheckScheduleSummary';

const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6] as const;

interface SpotCheckScheduleFieldsProps {
  spotCheckForm: UseSpotCheckForm;
  timezoneId: string;
  calendarStartDay: number;
  isLivePreview: boolean;
  isScheduledDetailsVisible: boolean;
  setIsScheduledDetailsVisible: (visible: boolean) => void;
  startTimeErrorText?: string;
  disableStartTimeCheck: boolean;
  disableEditExpirationType: boolean;
}

export default function SpotCheckScheduleFields(props: SpotCheckScheduleFieldsProps) {
  const {
    spotCheckForm,
    timezoneId,
    calendarStartDay,
    isLivePreview,
    isScheduledDetailsVisible,
    setIsScheduledDetailsVisible,
    startTimeErrorText,
    disableStartTimeCheck,
    disableEditExpirationType,
  } = props;

  const locale = useLocale();
  const localeCode = useSelector((state) => state.localeState.localeCode);
  const pickerLocale = datePickerLocale(localeCode);

  const {
    startTime,
    setStartTime,
    recurrence,
    recurrenceOptions,
    setRecurrence,
    setWeeklyScheduleOption,
    weeklyScheduleOption,
    selectedDaysOfWeek,
    setSelectedDaysOfWeek,
    endTime,
    setEndTime,
    reoccurringCount,
    setReoccurringCount,
    selectedEndType,
    setSelectedEndType,
    fiveMinutesFromNow,
  } = spotCheckForm;

  const startMoment = moment.tz(startTime, timezoneId);
  const fallbackStartMoment = moment.tz(fiveMinutesFromNow ?? Date.now(), timezoneId);
  const finiteStartMoment = startMoment.isValid() ? startMoment : fallbackStartMoment;
  const startDay = dateToDay(finiteStartMoment.valueOf(), timezoneId);
  const startDateSelected = startDay ? dayToLocalDate(startDay) : undefined;
  const startTimeValue = finiteStartMoment.format('HH:mm:ss');

  const minStartDate = useMemo(() => {
    if (disableStartTimeCheck || !fiveMinutesFromNow) {
      return;
    }
    const day = dateToDay(fiveMinutesFromNow, timezoneId);
    return day ? dayToLocalDate(day) : undefined;
  }, [disableStartTimeCheck, fiveMinutesFromNow, timezoneId]);

  const maxExpiryDay = dateToDay(moment().add(2, 'year').toDate(), timezoneId);
  const maxExpiryDate = maxExpiryDay ? dayToLocalDate(maxExpiryDay) : undefined;
  const minExpiryDate = startDay ? dayToLocalDate(startDay) : undefined;
  const endDay = dateToDay(endTime, timezoneId);
  const endDateSelected = endDay ? dayToLocalDate(endDay) : undefined;

  const dayOfWeekToTextMap = useMemo(() => {
    const { sunday, monday, tuesday, wednesday, thursday, friday, saturday } =
      locale.notificationsPage;
    return {
      0: sunday,
      1: monday,
      2: tuesday,
      3: wednesday,
      4: thursday,
      5: friday,
      6: saturday,
    };
  }, [locale.notificationsPage]);

  const handleStartDateSelect = (date: Date | undefined) => {
    if (!date) {
      return;
    }
    const dayStr = moment(date).format('YYYY-MM-DD');
    const timeStr = finiteStartMoment.format('HH:mm:ss');
    const next = moment.tz(`${dayStr}T${timeStr}`, timezoneId);
    if (!next.isValid()) {
      return;
    }
    setStartTime(next.valueOf());
  };

  const handleStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      return;
    }
    const dayStr = finiteStartMoment.format('YYYY-MM-DD');
    const next = moment.tz(`${dayStr}T${event.target.value}`, timezoneId);
    if (!next.isValid()) {
      return;
    }
    setStartTime(next.valueOf());
  };

  const toggleDayOfWeek = (dayOfWeek: number) => {
    if (selectedDaysOfWeek.includes(dayOfWeek)) {
      setSelectedDaysOfWeek(selectedDaysOfWeek.filter((day) => day !== dayOfWeek).sort());
    } else {
      setSelectedDaysOfWeek([...selectedDaysOfWeek, dayOfWeek].sort());
    }
  };

  const [occurrenceDraft, setOccurrenceDraft] = React.useState<string | null>(null);

  React.useEffect(() => {
    setOccurrenceDraft(null);
  }, [reoccurringCount]);

  const handleOccurrenceChange = (raw: string) => {
    if (raw === '') {
      setOccurrenceDraft('');
      return;
    }
    const next = parseInt(raw, 10);
    if (!Number.isFinite(next)) {
      return;
    }
    setOccurrenceDraft(null);
    setReoccurringCount(next);
  };

  const showWeeklyScheduleOption =
    recurrence === REPEAT_EVERY_15MIN ||
    recurrence === REPEAT_EVERY_30MIN ||
    recurrence === REPEAT_HOURLY ||
    recurrence === REPEAT_EVERY_6H ||
    recurrence === REPEAT_EVERY_4H;

  return (
    <div className="tw flex min-w-0 flex-col gap-4">
      {isScheduledDetailsVisible ? (
        <>
          <div className="tw flex min-w-0 flex-col gap-2">
            <div className="tw flex min-w-0 flex-wrap items-center gap-2">
              <DatePicker
                mode="single"
                selected={startDateSelected}
                onSelect={handleStartDateSelect}
                weekStartsOn={calendarStartDay}
                locale={pickerLocale}
                minDate={minStartDate}
                className="tw min-w-[11rem] flex-1"
                disabled={false}
              />
              <Input
                type="time"
                step={1}
                value={startTimeValue}
                onChange={handleStartTimeChange}
                className="tw w-[9.5rem] shrink-0"
                aria-invalid={!!startTimeErrorText}
              />
            </div>
            {startTimeErrorText ? (
              <p className="text-status-red caption">{startTimeErrorText}</p>
            ) : null}
          </div>

          <div className="tw flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
            <SimpleSelect
              className="tw min-w-0 flex-1"
              value={recurrence}
              onChange={(value) => setRecurrence(String(value))}
              aria-label={locale.scheduledAIChat.frequency}
            >
              {recurrenceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SimpleSelect>

            {showWeeklyScheduleOption ? (
              <div className="tw flex min-w-0 flex-1 items-center gap-1">
                <SimpleSelect
                  className="tw min-w-0 flex-1"
                  data-testid="weekly-schedule-option"
                  value={weeklyScheduleOption}
                  onChange={(value) => setWeeklyScheduleOption(value as never)}
                  aria-label={locale.scheduledAIChat.hoursOfOperationInfo}
                >
                  {Object.entries(AI_CHAT_WEEKLY_SCHEDULE_OPTIONS).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      {locale.event[value.text]}
                    </SelectItem>
                  ))}
                </SimpleSelect>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="tw inline-flex shrink-0 text-foreground-muted">
                      <Info className="size-4" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="tw max-w-xs text-left">
                    {locale.scheduledAIChat.hoursOfOperationInfo}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : null}
          </div>

          {recurrence === REPEAT_DAILY ? (
            <div className="tw flex flex-wrap gap-3">
              {DAYS_OF_WEEK.map((dayOfWeekNumber) => {
                const checked = selectedDaysOfWeek.includes(dayOfWeekNumber);
                const id = `spot-check-day-${dayOfWeekNumber}`;
                return (
                  <label
                    key={dayOfWeekNumber}
                    htmlFor={id}
                    className="tw flex cursor-pointer items-center gap-2 text-foreground-primary label-sm"
                  >
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={() => toggleDayOfWeek(dayOfWeekNumber)}
                    />
                    {dayOfWeekToTextMap[dayOfWeekNumber]}
                  </label>
                );
              })}
            </div>
          ) : null}
        </>
      ) : isLivePreview ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="tw h-auto w-fit px-0 text-text-accent"
          asChild
        >
          <a href={settings.solinkUpgradeUrl} target="_blank" rel="noopener noreferrer">
            {locale.cloudStorage.upgrade}
          </a>
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="tw h-auto w-full justify-start whitespace-normal px-3 py-2 text-left font-normal text-foreground-primary"
          onClick={() => setIsScheduledDetailsVisible(true)}
        >
          {getFormattedScheduledSummary(
            startTime,
            endTime,
            reoccurringCount,
            selectedEndType,
            timezoneId,
            locale,
          )}
        </Button>
      )}

      {recurrence !== DOES_NOT_REPEAT && isScheduledDetailsVisible ? (
        <div className="tw flex min-w-0 flex-col gap-3">
          <p className="text-foreground-primary label-sm">{locale.scheduledAIChat.expiration}</p>
          <RadioGroup
            value={selectedEndType}
            onValueChange={(value) => setSelectedEndType(value as AiInspectionEndType)}
            className="tw gap-3"
            disabled={disableEditExpirationType}
          >
            <div className="tw flex flex-wrap items-center gap-3">
              <label className="tw flex min-w-[3.5rem] items-center gap-2 text-foreground-primary label-sm">
                <RadioGroupItem value={AI_INSPECTION_END_TYPE.date} />
                {locale.general.on}
              </label>
              <DatePicker
                mode="single"
                selected={endDateSelected}
                onSelect={(date) => {
                  if (!date) {
                    return;
                  }
                  const dayStr = moment(date).format('YYYY-MM-DD');
                  setEndTime(endOfDayToDate(dayStr, timezoneId)?.valueOf() ?? endTime);
                }}
                weekStartsOn={calendarStartDay}
                locale={pickerLocale}
                minDate={minExpiryDate}
                maxDate={maxExpiryDate}
                disabled={
                  disableEditExpirationType || selectedEndType !== AI_INSPECTION_END_TYPE.date
                }
                className="tw min-w-[11rem] flex-1"
              />
            </div>

            <div className="tw flex flex-wrap items-center gap-3">
              <label className="tw flex min-w-[3.5rem] items-center gap-2 text-foreground-primary label-sm">
                <RadioGroupItem value={AI_INSPECTION_END_TYPE.occurrence} />
                {locale.general.after}
              </label>
              <Input
                type="number"
                min={1}
                max={MAX_HOURLY_OCCURRENCES_PER_2_YEARS}
                value={occurrenceDraft ?? reoccurringCount}
                disabled={
                  disableEditExpirationType || selectedEndType !== AI_INSPECTION_END_TYPE.occurrence
                }
                onChange={(event) => handleOccurrenceChange(event.target.value)}
                onBlur={() => setOccurrenceDraft(null)}
                className="tw w-20"
                aria-label={locale.scheduledAIChat.occurrences}
              />
              <span className="text-foreground-primary body-sm">
                {locale.scheduledAIChat.occurrences}
              </span>
              {disableEditExpirationType ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="tw inline-flex text-foreground-muted">
                      <Info className="size-4" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="tw max-w-xs text-left">
                    {locale.scheduledAIChat.occurrencesInfoTooltip}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>

            <label className="tw flex items-center gap-2 text-foreground-primary label-sm">
              <RadioGroupItem value={AI_INSPECTION_END_TYPE.never} />
              {locale.scheduledAIChat.neverExpires}
            </label>
          </RadioGroup>
        </div>
      ) : null}
    </div>
  );
}
